import { inject, Injectable, NgZone } from '@angular/core'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { shareReplay } from 'rxjs/operators'
import { LoggerService } from 'src/app/data/services/application/logging/logger.service'
import { MessageTapeService } from 'src/app/data/services/components/message-tape/message-tape.service'
import { PulsarMessageTypeEnum } from 'src/app/domain/enums/application/pulsar-message-type.enum'
import { AuthErrorMessage, EmissionValidationEventMessage, HealthStatus, HealthStatusMessage, LockEventMessage, PingMessage, PulsarServerMessage, ServiceStatus, SessionMessage } from 'src/app/domain/interfaces/application/pulsar-health.interface'
import { DEFAULT_PULSAR_WEBSOCKET_CONFIG, PulsarWebSocketConfig } from 'src/app/domain/interfaces/application/pulsar-websocket-config.interface'
import { TokenService } from 'src/app/infra/auth/token/token.service'
import { environment } from 'src/environments/environment'

const WS_CLOSE_AUTH_ERROR = 4001

const VALID_HEALTH_STATUSES: HealthStatus[] = ['ok', 'degraded', 'down']

@Injectable({ providedIn: 'root' })
export class PulsarWebSocketService {
  private readonly tokenService = inject(TokenService)
  private readonly ngZone = inject(NgZone)
  private readonly logger = inject(LoggerService)
  private readonly messageTapeService = inject(MessageTapeService)
  private readonly config: PulsarWebSocketConfig = { ...DEFAULT_PULSAR_WEBSOCKET_CONFIG }

  private socket: WebSocket | null = null
  private showRestoredToastOnNextOpen = false
  private reconnectAttempts = 0
  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null
  private intentionalClose = false
  private connectionLostOrFailed = false
  private socketSessionAuthenticated = false
  private lastAuthenticateRefreshTokenSent: string | null = null

  private readonly healthStatusSubject = new BehaviorSubject<HealthStatusMessage | null>(null)
  private readonly authErrorSubject = new Subject<void>()
  private readonly lockEventSubject = new Subject<LockEventMessage>()
  private readonly emissionValidationEventSubject = new Subject<EmissionValidationEventMessage>()
  private readonly connectionStateSubject = new BehaviorSubject<boolean>(false)
  private readonly isOfflineSubject = new BehaviorSubject<boolean>(false)

  readonly healthStatus$: Observable<HealthStatusMessage | null> = this.healthStatusSubject.asObservable().pipe(shareReplay({ bufferSize: 1, refCount: true }))

  readonly authError$: Observable<void> = this.authErrorSubject.asObservable()
  readonly lockEvent$: Observable<LockEventMessage> = this.lockEventSubject.asObservable()
  readonly emissionValidationEvent$: Observable<EmissionValidationEventMessage> = this.emissionValidationEventSubject.asObservable()
  readonly connectionState$: Observable<boolean> = this.connectionStateSubject.asObservable().pipe(shareReplay({ bufferSize: 1, refCount: true }))

  readonly isOffline$: Observable<boolean> = this.isOfflineSubject.asObservable().pipe(shareReplay({ bufferSize: 1, refCount: true }))

  get isOffline(): boolean {
    return this.isOfflineSubject.value
  }

  private updateIsOffline(): void {
    const connected = this.connectionStateSubject.value
    const status = this.healthStatusSubject.value
    const offlineByStatus = !!status && status.overall_status === 'down'
    const offlineByConnectionLoss = !connected && this.connectionLostOrFailed && this.reconnectAttempts >= this.config.reconnectAttemptsBeforeOfflineScreen
    const offline = offlineByStatus || offlineByConnectionLoss
    this.ngZone.run(() => this.isOfflineSubject.next(offline))
  }

  get currentHealthStatus(): HealthStatusMessage | null {
    return this.healthStatusSubject.value
  }

  get services(): Record<string, ServiceStatus> {
    const status = this.healthStatusSubject.value
    return status?.services ?? {}
  }

  public connect(): void {
    this.intentionalClose = false
    this.reconnectAttempts = 0
    this.connectInternal()
  }

  public syncSessionFromRefreshToken(): void {
    const refreshToken = this.tokenService.getRefreshToken()
    if (this.socket?.readyState === WebSocket.OPEN) {
      if (refreshToken) {
        if (this.lastAuthenticateRefreshTokenSent === refreshToken) {
          return
        }
        this.socket.send(JSON.stringify({ type: PulsarMessageTypeEnum.AUTHENTICATE, refresh_token: refreshToken }))
        this.lastAuthenticateRefreshTokenSent = refreshToken
      } else if (this.socketSessionAuthenticated) {
        this.socket.send(JSON.stringify({ type: PulsarMessageTypeEnum.LOGOUT }))
        this.socketSessionAuthenticated = false
        this.lastAuthenticateRefreshTokenSent = null
      }
    }
  }

  public notifyLogoutBeforeTokenClear(): void {
    if (this.socket?.readyState === WebSocket.OPEN && this.socketSessionAuthenticated) {
      this.socket.send(JSON.stringify({ type: PulsarMessageTypeEnum.LOGOUT }))
    }
    this.socketSessionAuthenticated = false
    this.lastAuthenticateRefreshTokenSent = null
  }

  public disconnect(): void {
    this.intentionalClose = true
    this.showRestoredToastOnNextOpen = false
    this.connectionLostOrFailed = false
    this.socketSessionAuthenticated = false
    this.lastAuthenticateRefreshTokenSent = null
    this.clearReconnectTimeout()
    if (this.socket) {
      this.socket.close()
      this.socket = null
    }
    this.connectionStateSubject.next(false)
    this.updateIsOffline()
  }

  public requestCurrentStatus(): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type: PulsarMessageTypeEnum.GET_CURRENT_STATUS }))
    }
  }

  private connectInternal(): void {
    if (this.socket?.readyState === WebSocket.OPEN) return
    if (this.socket?.readyState === WebSocket.CONNECTING) return

    const url = environment.pulsarWsUrl
    try {
      this.socket = new WebSocket(url)
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      this.logger.error('WebSocket constructor failed', { component: 'PulsarWebSocketService', error: message })
      this.connectionLostOrFailed = true
      this.connectionStateSubject.next(false)
      this.scheduleReconnect()
      this.updateIsOffline()
      return
    }

    this.socket.onopen = () => {
      this.reconnectAttempts = 0
      this.connectionLostOrFailed = false
      this.lastAuthenticateRefreshTokenSent = null
      this.connectionStateSubject.next(true)
      this.updateIsOffline()
      this.logger.debug('WebSocket connected', { component: 'PulsarWebSocketService' })
      this.syncSessionFromRefreshToken()
      if (this.showRestoredToastOnNextOpen) {
        this.showRestoredToastOnNextOpen = false
        this.messageTapeService.setTapeForSocketInterrupted(false)
      }
    }

    this.socket.onmessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data as string) as unknown
        const message = this.parseServerMessage(data)
        if (message) {
          switch (message.type) {
            case PulsarMessageTypeEnum.HEALTH_STATUS:
              this.healthStatusSubject.next(message)
              this.updateIsOffline()
              break
            case PulsarMessageTypeEnum.PING:
              this.sendPong()
              break
            case PulsarMessageTypeEnum.SESSION:
              this.socketSessionAuthenticated = message.authenticated
              if (!message.authenticated) {
                this.lastAuthenticateRefreshTokenSent = null
              }
              break
            case PulsarMessageTypeEnum.AUTH_ERROR:
              this.logger.warn('WebSocket auth_error', { component: 'PulsarWebSocketService', message: message.message })
              this.socketSessionAuthenticated = false
              this.lastAuthenticateRefreshTokenSent = null
              this.authErrorSubject.next()
              break
            case PulsarMessageTypeEnum.EMISSION_VALIDATION_EVENT:
              this.ngZone.run(() => this.emissionValidationEventSubject.next(message))
              break
            case PulsarMessageTypeEnum.LOCK_EVENT:
              this.lockEventSubject.next(message)
              break
          }
        } else {
          this.logger.warn('WebSocket received unknown or invalid message', { component: 'PulsarWebSocketService', hasData: !!data })
        }
      } catch (parseError) {
        const message = parseError instanceof Error ? parseError.message : String(parseError)
        this.logger.error('WebSocket message parse error', { component: 'PulsarWebSocketService', error: message })
      }
    }

    this.socket.onclose = (event: CloseEvent) => {
      this.socket = null
      this.socketSessionAuthenticated = false
      this.lastAuthenticateRefreshTokenSent = null
      this.connectionStateSubject.next(false)
      if (!this.intentionalClose) {
        this.connectionLostOrFailed = true
      }

      if (this.intentionalClose) {
        this.updateIsOffline()
        return
      }

      if (event.code === WS_CLOSE_AUTH_ERROR) {
        this.logger.warn('WebSocket auth error (4001)', { component: 'PulsarWebSocketService' })
        this.authErrorSubject.next()
        this.updateIsOffline()
        return
      }

      this.showRestoredToastOnNextOpen = true
      this.messageTapeService.setTapeForSocketInterrupted(true)
      this.scheduleReconnect()
      this.updateIsOffline()
    }

    this.socket.onerror = () => {
      this.connectionLostOrFailed = true
      this.connectionStateSubject.next(false)
      this.updateIsOffline()
    }
  }

  private sendPong(): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type: PulsarMessageTypeEnum.PONG }))
    }
  }

  private scheduleReconnect(): void {
    this.clearReconnectTimeout()
    this.reconnectAttempts += 1
    const delay = this.config.reconnectDelayMs
    this.logger.debug('WebSocket scheduling reconnect', { component: 'PulsarWebSocketService', attempt: this.reconnectAttempts, delayMs: delay })
    this.reconnectTimeoutId = setTimeout(() => {
      this.reconnectTimeoutId = null
      this.connectInternal()
    }, delay)
  }

  private clearReconnectTimeout(): void {
    if (this.reconnectTimeoutId !== null) {
      clearTimeout(this.reconnectTimeoutId)
      this.reconnectTimeoutId = null
    }
  }

  private parseServerMessage(raw: unknown): PulsarServerMessage | null {
    if (this.isHealthStatusMessage(raw)) return raw
    if (this.isPingMessage(raw)) return raw
    if (this.isSessionMessage(raw)) return raw
    if (this.isAuthErrorMessage(raw)) return raw
    if (this.isLockEventMessage(raw)) return raw
    if (this.isEmissionValidationEventMessage(raw)) return raw
    return null
  }

  private isServiceStatus(value: unknown): value is ServiceStatus {
    if (!value || typeof value !== 'object') return false
    const o = value as Record<string, unknown>
    return VALID_HEALTH_STATUSES.includes(o['status'] as HealthStatus) && typeof o['consecutive_failures'] === 'number' && (o['last_check'] === null || typeof o['last_check'] === 'string') && (o['response_time_ms'] === null || typeof o['response_time_ms'] === 'number') && (o['error'] === null || typeof o['error'] === 'string')
  }

  private isHealthStatusMessage(value: unknown): value is HealthStatusMessage {
    if (!value || typeof value !== 'object') return false
    const o = value as Record<string, unknown>
    if (o['type'] !== PulsarMessageTypeEnum.HEALTH_STATUS) return false
    if (typeof o['timestamp'] !== 'string') return false
    if (!VALID_HEALTH_STATUSES.includes(o['overall_status'] as HealthStatus)) return false
    if (!o['services'] || typeof o['services'] !== 'object') return false
    for (const v of Object.values(o['services'] as Record<string, unknown>)) {
      if (!this.isServiceStatus(v)) return false
    }
    return true
  }

  private isPingMessage(value: unknown): value is PingMessage {
    return !!value && typeof value === 'object' && (value as Record<string, unknown>)['type'] === PulsarMessageTypeEnum.PING
  }

  private isSessionMessage(value: unknown): value is SessionMessage {
    if (!value || typeof value !== 'object') return false
    const o = value as Record<string, unknown>
    if (o['type'] !== PulsarMessageTypeEnum.SESSION) return false
    if (typeof o['authenticated'] !== 'boolean') return false
    if (o['user_id'] !== undefined && typeof o['user_id'] !== 'string') return false
    return true
  }

  private isAuthErrorMessage(value: unknown): value is AuthErrorMessage {
    if (!value || typeof value !== 'object') return false
    const o = value as Record<string, unknown>
    return o['type'] === PulsarMessageTypeEnum.AUTH_ERROR && typeof o['message'] === 'string'
  }

  private isEmissionValidationEventMessage(value: unknown): value is EmissionValidationEventMessage {
    if (!value || typeof value !== 'object') return false
    const o = value as Record<string, unknown>
    if (o['type'] !== PulsarMessageTypeEnum.EMISSION_VALIDATION_EVENT) return false
    if (typeof o['order_id'] !== 'number') return false
    if (typeof o['emission_status_id'] !== 'number') return false
    if (!Array.isArray(o['emission_error_list']) || o['emission_error_list'].some((error) => typeof error !== 'string')) return false
    if (o['recipient_user_ids'] != null && (!Array.isArray(o['recipient_user_ids']) || o['recipient_user_ids'].some((userId) => typeof userId !== 'number'))) return false
    return true
  }

  private isLockEventMessage(value: unknown): value is LockEventMessage {
    if (!value || typeof value !== 'object') return false
    const o = value as Record<string, unknown>
    if (o['type'] !== 'lock_event') return false
    if (typeof o['event'] !== 'string') return false
    if (typeof o['resource_type'] !== 'string') return false
    if (typeof o['resource_id'] !== 'number') return false
    if (typeof o['locked_by_user_id'] !== 'number') return false
    if (o['recipient_user_ids'] != null && (!Array.isArray(o['recipient_user_ids']) || o['recipient_user_ids'].some((userId) => typeof userId !== 'number'))) return false
    if (o['message'] != null && typeof o['message'] !== 'string') return false
    return true
  }
}
